import { useFragment } from '@apollo/client';
import { WorkspaceLimit } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';

type UseWorkspaceLimitReturn = {
  workspaceLimit: number | null | undefined;
  isUnlimited: boolean;
  isLoading: boolean;
  isAtLimit: (workspaceCount: number) => boolean;
  shouldShowCreateButton: (canCreateWorkspace: boolean) => boolean;
  isCreateButtonDisabled: (workspaceCount: number) => boolean;
  limitReachedTooltip: string;
};

export function useWorkspaceLimit(): UseWorkspaceLimitReturn {
  const accountId = useActiveAccountId();

  const { complete, data } = useFragment({
    fragment: WorkspaceLimit,
    fragmentName: 'WorkspaceLimit',
    from: {
      __typename: 'Account',
      id: accountId,
    },
  });

  const workspaceLimit = complete
    ? data?.subscription?.limitedResources?.workspaces?.limit53
    : undefined;

  // Only null is truly unlimited; undefined means data not loaded yet
  const isUnlimited = workspaceLimit === null;

  const isAtLimit = (workspaceCount: number): boolean => {
    // If data not loaded, we don't know yet
    if (workspaceLimit === undefined) {
      return false;
    }
    return !isUnlimited && workspaceCount >= workspaceLimit;
  };

  const shouldShowCreateButton = (canCreateWorkspace: boolean): boolean => {
    // Hide if data not loaded yet (safe default)
    if (workspaceLimit === undefined) {
      return false;
    }
    // Show if unlimited or limit > 1, and user has permission
    return (isUnlimited || workspaceLimit > 1) && canCreateWorkspace;
  };

  const isCreateButtonDisabled = (workspaceCount: number): boolean => {
    // Disable if data not loaded (safe default)
    if (workspaceLimit === undefined) {
      return true;
    }
    return isAtLimit(workspaceCount);
  };

  return {
    workspaceLimit,
    isUnlimited,
    isLoading: workspaceLimit === undefined,
    isAtLimit,
    shouldShowCreateButton,
    isCreateButtonDisabled,
    limitReachedTooltip: "You've reached the account's Workspace limit",
  };
}
